'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  useCallback,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { Person, PersonName } from '~/lib/types/person';
import type { Application } from '~/lib/types/application';
import type { AuthTokens } from '~/lib/types/auth';
import { defaultEmployerAddress, defaultPerson, defaultPersonForm } from '~/lib/types/person';
import { defaultApplication } from '~/lib/types/application';
import { getMemberAccount } from '../api/account-helper';
// BS-2161: dynamic import for sonner. Static `import { toast } from 'sonner'`
// pulled the whole ~68 KB sonner module into the shared layout chunk for
// every route, including `/`, `/_not-found`, and `/gsap`, which never fire
// a toast. `toastLazy.*` is a thin wrapper that imports `sonner` on first
// use, so the chunk shifts out of First Load JS and only loads when a toast
// is actually queued.
import { toastLazy as toast } from '~/lib/toast-lazy';
import { postLoanApplication, type LoanAppPostBody } from '../api/application-helper';
import { getReferralStatus } from '../api/referral-helper';
import {
  REFERRAL_ID_PATTERN,
  clearReferralCookie,
  readReferralCookie,
  writeReferralCookie,
} from '../referral-cookie';
// BS-2151: emit submit_attempt/success/failure here because the
// API-side outcome (success/error) is only observable from inside
// handleFormSubmission. The summary page emits step_view via
// useStepAnalytics; only the submit-outcome events need FormProvider
// integration. Keep this minimal: no other behavior changes.
// BS-2152: emitSessionLinked is called inside handleLoginSuccess once
// the member account number is known (post-login account fetch).
import { clearSessionId, emitEvent, emitSessionLinked, flushNow } from '../analytics';
// Import and re-export from the zero-dependency constants module so E2E specs
// can import FORM_CONTEXT_VERSION without pulling in React or Next.js
// (BS-2170 #3). The local binding is still available for use inside this file.
import { FORM_CONTEXT_VERSION } from '~/lib/consts/form-version';
export { FORM_CONTEXT_VERSION };

/**
 * sessionStorage marker: set after showing the cross-deploy migration toast
 * so the toast fires at most once per browser session, regardless of how many
 * times the FormProvider mounts (e.g. HMR, route changes).
 */
const MIGRATION_TOAST_SHOWN_KEY = 'ola_migration_toast_shown';

// Session storage keys for state persistence (survives page reloads from 431 errors)
const STORAGE_KEYS = {
  APPLICATION: 'loan-app-application',
  PEOPLE: 'loan-app-people',
  BRANCH_PREFERENCE: 'loan-app-branch',
  ACCOUNT_NUMBER: 'loan-app-account',
  IS_EMPLOYEE: 'loan-app-is-employee',
  STEP_INDEX: 'loan-app-step-index',
  // Referral attribution deliberately does NOT live here: it persists in a
  // first-party cookie (see ~/lib/referral-cookie) so it survives the member
  // closing the tab and returning later, and so form resets / schema-version
  // clears never wipe attribution.
  /** Stores the schema version of the currently persisted data set. */
  SCHEMA_VERSION: 'loan-app-schema-version',
} as const;

// Helper to safely parse JSON from sessionStorage
function getStoredValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as T;
    }
  } catch {
    // Silently fail - return default value
  }
  return defaultValue;
}

// Helper to safely store value in sessionStorage
function setStoredValue<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail - storage not critical
  }
}

/**
 * Reads the persisted schema version from sessionStorage and returns whether
 * it matches FORM_CONTEXT_VERSION. Returns `true` (match) when there is no
 * stored data at all (fresh session - no migration needed).
 *
 * Exported for unit testing.
 */
export function checkStoredVersion(): boolean {
  if (typeof window === 'undefined') return true;
  const stored = sessionStorage.getItem(STORAGE_KEYS.SCHEMA_VERSION);
  // No version key at all → either a brand-new session (no data) or legacy
  // data written before versioning was added. Treat as mismatch only when
  // other form keys are present (legacy payload).
  if (stored === null) {
    const hasLegacyData = Object.values(STORAGE_KEYS)
      .filter((k) => k !== STORAGE_KEYS.SCHEMA_VERSION)
      .some((k) => sessionStorage.getItem(k) !== null);
    return !hasLegacyData; // fresh session → match; legacy data → mismatch
  }
  return Number(stored) === FORM_CONTEXT_VERSION;
}

/**
 * Clears all form-related sessionStorage keys (both data and the version
 * marker). Used when a version mismatch is detected on mount.
 *
 * Exported for unit testing.
 */
export function clearFormStorage(): void {
  if (typeof window === 'undefined') return;
  Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Shows the cross-deploy migration toast at most once per browser session.
 * Uses a sessionStorage marker so re-mounting the FormProvider (e.g. HMR,
 * Next.js route transitions) doesn't re-fire the toast.
 *
 * Sets the marker optimistically (before the toast fires) so two rapid
 * synchronous calls deduplicate. If the lazy sonner chunk fails to load
 * (deploy mid-fetch, network blip), the .catch rolls the marker back
 * so the next visit retries - BS-2149's cross-deploy migration story
 * relies on the member seeing the "we cleared your data" notice, and a
 * permanently-set marker on chunk-load failure would suppress every
 * future retry while the member sees nothing.
 *
 * Exported for unit testing.
 */
export function showMigrationToastOnce(): void {
  if (typeof window === 'undefined') return;
  if (sessionStorage.getItem(MIGRATION_TOAST_SHOWN_KEY)) return;
  sessionStorage.setItem(MIGRATION_TOAST_SHOWN_KEY, '1');
  toast
    .info("We've improved this form since your last visit. Please re-enter your information.")
    .catch(() => {
      // Chunk load failed. Roll the marker back so the next visit
      // retries showing the notice. The user saw nothing this time,
      // but they will not be permanently silenced.
      sessionStorage.removeItem(MIGRATION_TOAST_SHOWN_KEY);
    });
}

type FormContextType = {
  people: Person[];
  application: Application;
  steps: string[];
  handleLoginSuccess: (tokens: AuthTokens) => Promise<void>;
  handleFormSubmission: () => Promise<void>;
  setPeople: Dispatch<SetStateAction<Person[]>>;
  setApplication: Dispatch<SetStateAction<Application>>;
  resetForm: () => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  hasSubmitted: boolean;
  clearSubmissionState: () => void;
  branchPreference: string;
  setBranchPreference: Dispatch<SetStateAction<string>>;
  /**
   * Employee UUID captured from a `?ref=<id>` URL param when the member
   * landed on OLA via a personalized referral link. Persisted to a
   * first-party cookie (30-day rolling expiry) so attribution survives
   * the member leaving and returning later. Last link click wins;
   * consumed (cleared) on successful submission. Empty string when no
   * referral is active or it has been invalidated.
   */
  referringEmployeeId: string;
  /**
   * Display name of the referring employee, fetched from the API
   * after the referral has been validated against the active
   * employee directory. Empty string until validation completes or
   * if the referral is inactive. Used to surface a trust signal on
   * the form ("Your application will be sent to {name}").
   */
  referringEmployeeName: string;
};

const FormContext = createContext<FormContextType | undefined>(undefined);

export function FormProvider({ children }: { children: ReactNode }) {
  const path = usePathname();
  const searchParams = useSearchParams();
  // Use state (not a ref) for the hydration flag so persistence-effect
  // closures see the correct value across StrictMode's intentional
  // double-invoke of setup-effects in dev. With a ref, the flag mutation
  // inside the hydrate effect would flip the ref to `true` mid-mount; on
  // the StrictMode re-setup pass, each persistence effect would observe
  // `isInitialized.current === true` while its `application` / `people`
  // closure still held the pre-hydrate defaults, and write the defaults
  // back over the user's data. Tracking initialization through state
  // captures the flag in the closure at render time so the flag and the
  // data are always in sync (BS-2180).
  const [isInitialized, setIsInitialized] = useState(false);

  // Always start from static defaults so SSR HTML matches the first client
  // render. sessionStorage is read in a useEffect below; storage-backed
  // values land on a second client render to avoid hydration mismatches.
  const [people, setPeople] = useState<Person[]>([
    { ...defaultPerson, isBorrower: true, index: 0 },
  ]);
  const [application, setApplication] = useState<Application>(defaultApplication);
  const [accountNumber, setAccountNumber] = useState('');
  const [isEmployeeAccount, setIsEmployeeAccount] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [branchPreference, setBranchPreference] = useState('');
  const [referringEmployeeId, setReferringEmployeeId] = useState('');
  const [referringEmployeeName, setReferringEmployeeName] = useState('');

  const router = useRouter();

  // Persist state to sessionStorage whenever it changes (survives 431-induced reloads).
  // SCHEMA_VERSION is stamped once in the hydrate effect on mount (both the
  // match and mismatch branches), so each persistence effect only writes its
  // own data key. Earlier shapes that stamped SCHEMA_VERSION only from this
  // effect created a window where touching people/branchPreference/etc. first
  // would persist data without a version stamp, which the next mount would
  // then flag as a legacy payload and fire a false migration toast.
  useEffect(() => {
    if (isInitialized) {
      setStoredValue(STORAGE_KEYS.APPLICATION, application);
    }
  }, [application, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      setStoredValue(STORAGE_KEYS.PEOPLE, people);
    }
  }, [people, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      setStoredValue(STORAGE_KEYS.BRANCH_PREFERENCE, branchPreference);
    }
  }, [branchPreference, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      setStoredValue(STORAGE_KEYS.ACCOUNT_NUMBER, accountNumber);
    }
  }, [accountNumber, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      setStoredValue(STORAGE_KEYS.IS_EMPLOYEE, isEmployeeAccount);
    }
  }, [isEmployeeAccount, isInitialized]);

  // Mirror the referral into its first-party cookie once hydration has
  // settled. A non-empty id writes the cookie (refreshing the rolling
  // 30-day expiry on every visit); an empty id clears it, which is how
  // an invalidated referral (inactive employee) and a consumed referral
  // (successful submission) both propagate to the cookie.
  useEffect(() => {
    if (!isInitialized) return;
    if (referringEmployeeId) {
      writeReferralCookie(referringEmployeeId);
    } else {
      clearReferralCookie();
    }
  }, [referringEmployeeId, isInitialized]);

  // Capture `?ref=<employeeId>` on entry. URL param wins over any prior
  // stored value (fresh referral supersedes a stale one). Silently
  // drops malformed values so attackers can't plant arbitrary payloads.
  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam && REFERRAL_ID_PATTERN.test(refParam)) {
      setReferringEmployeeId(refParam);
    }
  }, [searchParams]);

  // Hydrate the referral from its cookie on mount. This MUST be a
  // functional update: the URL-capture effect above is declared first,
  // so its setter is already queued in the same mount flush when this
  // one runs, and `prev` sees the freshly captured URL value. A plain
  // `setReferringEmployeeId(readReferralCookie())` would overwrite the
  // URL capture with the (empty, for a first-time visitor) stored value
  // — the exact bug that sent every cold-load referral to the general
  // queue when this state hydrated from sessionStorage.
  useEffect(() => {
    setReferringEmployeeId((prev) => prev || readReferralCookie());
  }, []);

  // Whenever a referring employee id is set - either from the URL above
  // or hydrated from the referral cookie on mount - validate it against
  // the active-employee directory. If the employee no longer exists or
  // has been soft-deleted, clear the referral so the applicant falls back
  // through the normal flow (including the Preferred Branch selector).
  // Stale links must not silently misroute applications.
  useEffect(() => {
    if (!referringEmployeeId) {
      setReferringEmployeeName('');
      return;
    }
    let cancelled = false;
    void getReferralStatus(referringEmployeeId).then((status) => {
      if (cancelled) return;
      if (status.active && status.displayName) {
        setReferringEmployeeName(status.displayName);
        // Valid referral means the app is routed to the referring
        // employee directly; no branch preference is needed.
        setBranchPreference('');
      } else {
        setReferringEmployeeId('');
        setReferringEmployeeName('');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [referringEmployeeId]);

  // Hydrate persisted state from sessionStorage on mount. Mirrors the
  // initial-state mapping above. Flipping isInitialized to `true` after
  // these updates lets the persistence effects fire on subsequent user
  // changes without writing the static defaults back over stored values
  // on first render. Because isInitialized is now state (BS-2180) the
  // persistence effects re-run after the flag flips with a fresh closure
  // that sees the hydrated data, not the pre-hydrate defaults.
  //
  // Version check: if the stored schema version doesn't match
  // FORM_CONTEXT_VERSION (including legacy payloads with no version key),
  // we clear sessionStorage and show a one-time toast rather than hydrating
  // a potentially incompatible shape. The member loses in-progress data but
  // the form is guaranteed to be in a consistent state.
  useEffect(() => {
    if (!checkStoredVersion()) {
      clearFormStorage();
      showMigrationToastOnce();
      // Stamp the current version so the next mount sees a clean match
      // even if the user only touches people/branchPreference/etc. (which
      // wouldn't otherwise re-stamp). Pairs the version with the freshly
      // cleared storage state.
      setStoredValue(STORAGE_KEYS.SCHEMA_VERSION, FORM_CONTEXT_VERSION);
      setIsInitialized(true);
      return;
    }

    setPeople(
      getStoredValue(STORAGE_KEYS.PEOPLE, [{ ...defaultPerson, isBorrower: true, index: 0 }])
    );
    setApplication(getStoredValue(STORAGE_KEYS.APPLICATION, defaultApplication));
    setAccountNumber(getStoredValue(STORAGE_KEYS.ACCOUNT_NUMBER, ''));
    setIsEmployeeAccount(getStoredValue(STORAGE_KEYS.IS_EMPLOYEE, false));
    setBranchPreference(getStoredValue(STORAGE_KEYS.BRANCH_PREFERENCE, ''));
    // Stamp the version on every successful hydrate. Fresh sessions also
    // pass checkStoredVersion (no data + no version = match), so this
    // ensures the version is always present before any data write fires.
    // Without this, a fresh user touching people/branchPreference first
    // would persist data without a version and the next mount would
    // misread it as legacy.
    setStoredValue(STORAGE_KEYS.SCHEMA_VERSION, FORM_CONTEXT_VERSION);
    setIsInitialized(true);
  }, []);

  const steps = useMemo(() => {
    const borrowers = people.filter((p) => p.isBorrower);

    const basePaths = [
      '/login',
      '/loan-selection',
      '/0/personal-info',
      '/0/residence-info',
      '/0/employment',
      '/co-borrower/selection',
    ];

    const additionalPaths = borrowers
      .slice(1)
      .flatMap(({ index }) => [
        `/${index}/personal-info`,
        `/${index}/residence-info`,
        `/${index}/employment`,
      ]);

    const finalPaths = ['/references', '/loan-details', '/summary', '/confirmation'];

    return [...basePaths, ...additionalPaths, ...finalPaths];
  }, [people]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const resetForm = useCallback(() => {
    setPeople([{ ...defaultPerson, isBorrower: true, index: 0 }]);
    setApplication(defaultApplication);
    setHasSubmitted(false);
    setCurrentStepIndex(0);
    setBranchPreference('');
    // referringEmployeeId / referringEmployeeName deliberately survive a
    // form reset: attribution is not form data. The member still arrived
    // via the referral, so restarting the form keeps the credit. The
    // referral is consumed on successful submission instead.
    // Clear sessionStorage to prevent stale state on next session
    if (typeof window !== 'undefined') {
      Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.removeItem(MIGRATION_TOAST_SHOWN_KEY);
      // BS-2182: re-stamp SCHEMA_VERSION after clearing. The state setters
      // above schedule a re-render; the persistence effects (gated on
      // `isInitialized === true`, which is the case here since reset is
      // only callable post-hydrate) will reliably fire on that render and
      // write the in-memory defaults back to sessionStorage for the data
      // keys. Without this re-stamp, sessionStorage would end up with
      // data keys present but no SCHEMA_VERSION - which the next mount's
      // checkStoredVersion() classifies as a legacy payload, firing the
      // cross-deploy migration toast even though no schema migration
      // occurred. The defaults ARE the current schema, so stamping the
      // current version pairs the persisted state with a consistent
      // version marker. (Future maintainer: do not remove this line -
      // see BS-2182 if you're tempted to.)
      setStoredValue(STORAGE_KEYS.SCHEMA_VERSION, FORM_CONTEXT_VERSION);
    }
    // BS-2151: clear the analytics session so the next funnel attempt
    // gets a fresh session_id. PSA's per-session rollup expects each
    // attempt to be its own row.
    clearSessionId();
  }, [setPeople, setApplication]);

  const goToNextStep = () => {
    // Prevents multi clicks from skipping ahead without rendering the next page first
    const expectedPath = steps[currentStepIndex];
    if (expectedPath !== path) {
      return;
    }

    const nextIndex = currentStepIndex + 1;
    setCurrentStepIndex(nextIndex);
    const nextPath = steps[nextIndex];
    if (nextPath) {
      router.push(nextPath);
    }
  };

  const goToPreviousStep = () => {
    const nextIndex = currentStepIndex - 1;
    setCurrentStepIndex(nextIndex);
    const prevPath = steps[nextIndex];
    if (prevPath) {
      router.push(prevPath);
    }
  };

  const createPersonObjs = (account: {
    Account: { Number: string; Restrict: number };
    Names: PersonName[];
  }) => {
    const newPeople = account.Names.map((name, index) => ({
      index,
      form: {
        ...defaultPersonForm,
        memberInfo: {
          ...defaultPersonForm.memberInfo,
          basicInfo: {
            ...defaultPersonForm.memberInfo.basicInfo,
            first: name.First ?? '',
            middle: name.Middle ?? '',
            last: name.Last ?? '',
            ssn: name.Ssn ?? '',
            birthDate: name.BirthDate ? new Date(name.BirthDate).toISOString().split('T')[0]! : '',
            suffix: name.Suffix ?? '',
            code: name.Type === 0 ? 0 : 3,
            // ID type / number: the Ident* fields are multi-entry; prefill the
            // first ID on file. IdentIdType shares Symitar's code list with the
            // personal-info identityType select, so the code maps directly.
            identityType: name.IdentIdType?.[0]?.IdentIdType ?? 0,
            identityNumber: name.IdentIdNumber?.[0]?.IdentIdNumber ?? '',
          },
          // Pre-fill contact + current address from the member record so the
          // personal-info and residence-info steps land populated. The data is
          // returned by getMemberAccountFields; it just wasn't being mapped.
          contact: {
            ...defaultPersonForm.memberInfo.contact,
            email: name.Email ?? '',
            homePhone: name.HomePhone ?? '',
            mobilePhone: name.MobilePhone ?? '',
          },
          address: {
            ...defaultPersonForm.memberInfo.address,
            street: name.Street ?? '',
            // ExtraAddress is the unit / apt line (residence-info "extra" input).
            extra: name.ExtraAddress ?? '',
            city: name.City ?? '',
            state: name.State ?? '',
            zip: name.ZipCode ?? '',
          },
          // UsPersonFlag is Symitar's US-person indicator (1 = US person); the
          // submission maps usCitizen === 1 to the disclosure boolean.
          disclosures: {
            ...defaultPersonForm.memberInfo.disclosures,
            usCitizen: name.UsPersonFlag ?? 0,
          },
        },
        // Current gross monthly pay drives the employment step's gross-pay
        // input; stamp the duration so it reads as a monthly figure.
        income: {
          ...defaultPersonForm.income,
          grossPay: name.CurrGrossMonthPay ?? 0,
          grossPayDuration: 'monthly',
        },
      },
      wasFetched: true,
      name,
      isBorrower: name.Type === 0,
      employerAddress: defaultEmployerAddress,
      autoCompletedAddress: '',
      autoCompletedEmployerAddress: '',
      autoCompletedPrevEmployerAddress: '',
    }));
    setPeople(newPeople);
    setAccountNumber(account.Account.Number);
    setIsEmployeeAccount(account.Account.Restrict === 3);
  };
  const handleLoginSuccess = async (tokens: AuthTokens) => {
    try {
      const resp = await getMemberAccount(tokens.token);
      if (resp.account) {
        createPersonObjs(resp.account);
        // BS-2176 (race fix): flush any buffered analytics events before
        // emitting the session link. Login can complete in <1s, which is
        // faster than the 1s debounce that creates the session row on the
        // server. If we call emitSessionLinked before the first ingest
        // POST, linkSession silently no-ops because the row doesn't exist
        // yet, and the link is lost for fast logins.
        //
        // awaiting flushNow() forces the ingest POST through first
        // (creating the session row), then the link POST has a row to
        // UPDATE. flushNow is non-throwing and bounded by a 2-second
        // timeout (AbortController inside flushViaFetch, BS-2176), so
        // a stalled analytics endpoint resolves in <=2s and never
        // blocks form flow. On timeout the batch is re-queued for the
        // next flush.
        await flushNow();
        // BS-2152: emit session_linked now that we have the member account
        // number. Fire-and-forget - the analytics path must not block the
        // navigation or surface errors to the member.
        void emitSessionLinked(resp.account.Account.Number);
        goToNextStep();
      } else {
        toast.error('There was an issue gathering existing information.');
      }
    } catch {
      toast.error('There was an issue gathering existing information.');
    }
  };

  const handleFormSubmission = async () => {
    // Prevent resubmission if already submitted
    if (hasSubmitted) {
      toast.error('Application has already been submitted. Please start a new application.');
      router.push('/login');
      return;
    }

    // BS-2151: submit_attempt fires on the user-initiated submit
    // click, regardless of API outcome. submit_success / submit_failure
    // below split on the API response. The loan_type label gives PSA
    // a per-product submit funnel without joining against the events
    // table.
    const loanTypeLabel =
      application.loanType !== undefined ? String(application.loanType) : undefined;
    emitEvent('submit_attempt', 'summary', { loan_type: loanTypeLabel });

    try {
      const body: LoanAppPostBody = {
        account: accountNumber,
        application,
        isEmployeeAccount,
        persons: people.filter((person) => person.isBorrower).map((person) => person.form),
        ...(branchPreference && { branch: branchPreference }),
        ...(referringEmployeeId && { referringEmployeeId }),
      };

      const resp = await postLoanApplication(body);
      if (resp.success) {
        // BS-2151: submit_success is the terminal happy-path event.
        // step_complete fires alongside so the funnel rollup sees
        // "summary completed" in addition to the application-level
        // success signal.
        emitEvent('submit_success', 'summary', { loan_type: loanTypeLabel });
        emitEvent('step_complete', 'summary', { loan_type: loanTypeLabel });
        // Force-flush so the terminal events make it to the server
        // before the user navigates away. flushNow is non-throwing and
        // bounded by a 2-second timeout (BS-2176).
        void flushNow();
        // BS-2183: second `session_linked` event with the freshly-minted
        // application UUID so the API back-fills
        // `online_application_sessions.linked_application_id`. The first
        // emit (in handleLoginSuccess) ran before the application existed,
        // so the column has been NULL up to this point. The service's
        // COALESCE write-once pattern accepts the second write into a
        // NULL column but rejects any future attempts to overwrite a
        // real value. Fire-and-forget, never blocks the form.
        //
        // resp.applicationId is undefined on parse failure or when the
        // server response shape drifts - skip the link back-fill in that
        // case rather than emitting an event with a missing field.
        if (resp.applicationId) {
          void emitSessionLinked(accountNumber, resp.applicationId);
        }
        // Consume the referral: one link click credits at most one
        // submitted application. Clearing the state cascades to the
        // cookie via the mirror effect, so a member who starts another
        // application later is unattributed until they click a fresh
        // referral link.
        setReferringEmployeeId('');
        setReferringEmployeeName('');
        setHasSubmitted(true);
        goToNextStep();
      } else {
        emitEvent('submit_failure', 'summary', { loan_type: loanTypeLabel });
        toast.error('There was an issue submitting your application.');
      }
    } catch {
      emitEvent('submit_failure', 'summary', { loan_type: loanTypeLabel });
      toast.error('There was an issue submitting your application.');
    }
  };

  // Sync currentStepIndex with the current path when path changes (e.g., browser navigation)
  useEffect(() => {
    const currentIndex = steps.findIndex((step) => step === path);
    if (currentIndex !== -1) {
      setCurrentStepIndex(currentIndex);
    }
  }, [path, steps]);

  useEffect(() => {
    // Only redirect to login if we're at the root path
    if (path === '/') {
      router.push(steps[0]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); //on load, set page to login and clear all values. We can remove this in the future if we want to persist the page

  const clearSubmissionState = useCallback(() => {
    setHasSubmitted(false);
  }, []);

  const contextValue: FormContextType = {
    people,
    application,
    steps,
    handleFormSubmission,
    handleLoginSuccess,
    setPeople,
    setApplication,
    resetForm,
    goToNextStep,
    goToPreviousStep,
    hasSubmitted,
    clearSubmissionState,
    branchPreference,
    setBranchPreference,
    referringEmployeeId,
    referringEmployeeName,
  };

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
}

export function useFormContext() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}
